import React, {useState} from "react";
import {useApiQuery} from "../../../services/useApiQuery.ts";
import {
    Badge,
    Button,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from "@fluentui/react-components";
import {useTranslation} from "react-i18next";
import {ArrowSync24Regular, Checkmark24Regular, LockShield24Regular} from "@fluentui/react-icons";
import {useFetchNamesMutation} from "../../../services/useFetchNamesMutation.ts";

export interface StatusModalProps {
    groupId: string;
    isiOS: boolean;
}

export interface GroupMembers {
    [userId: string]: boolean;
}

export const AudienceMembersModal: React.FC<StatusModalProps> = (props: StatusModalProps) => {
    const {t} = useTranslation()
    const [userMap, setUserMap] = useState<Record<string, {name: string, email: string}>>({})

    const {
        data,
        isLoading,
        refetch,
        isRefetching
    } = useApiQuery<GroupMembers>(`/api/group/members/${props.groupId}`);

    const [namesRevealed, setNamesRevealed] = useState(false)

    const fetchNamesMutation = useFetchNamesMutation({
        onSuccess: (data) => {
            setNamesRevealed(true);
            setUserMap(data)
        },
        onError: (error) => {
            setNamesRevealed(false)
        },
    });

    const onNameRevealClick = () => {
        fetchNamesMutation.mutate(Object.keys(data!))
    }

    if (isLoading) return (
        <Spinner>{t('text.loading-participants')}</Spinner>
    )

    return (<>
        <div className="flex w-full">
            <Button
                className="ms-auto"
                appearance="subtle"
                icon={(isRefetching || isLoading) ?
                    <Spinner size="extra-tiny"/> : <ArrowSync24Regular/>}
                onClick={() => refetch()}
                disabled={isRefetching || isLoading}
            >
                {t("button.refresh")}
            </Button>
            <Button
                className="ms-auto"
                appearance="subtle"
                icon={(namesRevealed) ?
                    <Checkmark24Regular/> : (fetchNamesMutation.isPending ? <Spinner size="extra-tiny"/> : <LockShield24Regular/>)}
                onClick={() => onNameRevealClick()}
                disabled={namesRevealed || isRefetching || isLoading || !data || !Object.keys(data).length || props.isiOS}
            >
                {t("button.reveal-names")}
            </Button>
        </div>
        <Table aria-label="Audience Members">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>{t('text.user-id')}</TableHeaderCell>
                    {namesRevealed && <TableHeaderCell>{t('table.header.email')}</TableHeaderCell>}
                    <TableHeaderCell>{t('text.installation-status')}</TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {Object.entries(data!).map(([userId, status]) => (
                    <TableRow key={userId}>
                        {namesRevealed && <TableCell>{userMap[userId]?.name || userId}</TableCell>}
                        {namesRevealed && <TableCell style={{overflow: "hidden", textOverflow: "ellipsis"}}>{userMap[userId]?.email || '-'}</TableCell>}
                        {!namesRevealed && <TableCell>{userId}</TableCell>}
                        <TableCell>
                            <Badge
                                shape="rounded"
                                appearance="tint"
                                color={status ? "success" : "warning"}
                            >
                                {t(status ? "text.installed" : "text.not-installed")}
                            </Badge>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </>)

}